import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  buildInterviewStorageKey,
  clearInterviewCache,
  clearInterviewCachesForUser,
  clearLegacyInterviewStorage,
  readAuthUserId,
  readInterviewCache,
  writeInterviewCache,
} from '../services/sessionStorage';

export interface InterviewQuestion {
  id: number;
  interview_id: number;
  parent_question_id: number | null;
  question_index: number | null;
  question_text: string;
  question_type: 'intro' | 'main' | 'followup' | 'candidate_question';
  category: 'technical' | 'communication' | 'problem_solving';
}

export interface InterviewAnswer {
  id: number;
  question_id: number;
  answer_text: string;
  time_spent_seconds: number;
  score: number | null;
  ai_feedback: string;
  needs_followup: boolean;
  video_url?: string | null;
  video_mime_type?: string | null;
  video_size_bytes?: number | null;
  video_duration_seconds?: number | null;
}

export interface InterviewResult {
  id: number;
  interview_id: number;
  final_score: number;
  content_final_score?: number | null;
  confidence_score?: number | null;
  confidence_coverage_pct?: number;
  confidence_weight_pct?: number;
  is_provisional?: boolean;
  confidence_recomputed_at?: string | null;
  technical_score: number;
  communication_score: number;
  problem_solving_score: number;
  status: 'strong' | 'average' | 'weak';
  summary: string;
  strength: string;
  weakness: string;
}

export interface InterviewData {
  interviewId: number | null;
  jobId: number | null;
  outsourceId: number | null;
  questions: InterviewQuestion[];
  answers: InterviewAnswer[];
  result: InterviewResult | null;
  status: 'idle' | 'in_progress' | 'completed' | 'technical_interrupted';
}

type InterviewScope = {
  userId: string | null;
  jobId: number | null;
  outsourceId: number | null;
};

interface InterviewContextType {
  interviewData: InterviewData;
  bindInterviewScope: (scope: { jobId: number | null; outsourceId: number | null }) => void;
  clearUserInterviewCaches: () => void;
  setInterviewId: (id: number) => void;
  setJobId: (id: number) => void;
  setOutsourceId: (id: number) => void;
  setQuestions: (questions: InterviewQuestion[]) => void;
  addQuestion: (question: InterviewQuestion, options?: { insertAfterQuestionId?: number | null }) => void;
  addAnswer: (answer: InterviewAnswer) => void;
  setResult: (result: InterviewResult) => void;
  setStatus: (status: InterviewData['status']) => void;
  resetInterview: () => void;
}

const defaultData: InterviewData = {
  interviewId: null,
  jobId: null,
  outsourceId: null,
  questions: [],
  answers: [],
  result: null,
  status: 'idle',
};

const hasEquivalentQuestion = (questions: InterviewQuestion[], question: InterviewQuestion) => {
  const questionId = Number(question?.id);
  const parentId = Number(question?.parent_question_id);

  return questions.some((item) => {
    if (Number.isFinite(questionId) && questionId > 0 && Number(item.id) === questionId) {
      return true;
    }

    return (
      question?.question_type === 'followup' &&
      item.question_type === 'followup' &&
      Number.isFinite(parentId) &&
      parentId > 0 &&
      Number(item.parent_question_id) === parentId
    );
  });
};

const InterviewContext = createContext<InterviewContextType | null>(null);

const normalizeScopeValue = (value: number | null | undefined) => {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : null;
};

const buildScopedDefaultData = (scope?: Partial<InterviewScope>): InterviewData => ({
  ...defaultData,
  jobId: normalizeScopeValue(scope?.jobId),
  outsourceId: normalizeScopeValue(scope?.outsourceId),
});

const normalizeInterviewData = (value: Partial<InterviewData> | null | undefined, scope?: Partial<InterviewScope>): InterviewData => ({
  interviewId: normalizeScopeValue(value?.interviewId),
  jobId: normalizeScopeValue(scope?.jobId ?? value?.jobId),
  outsourceId: normalizeScopeValue(scope?.outsourceId ?? value?.outsourceId),
  questions: Array.isArray(value?.questions) ? value!.questions : [],
  answers: Array.isArray(value?.answers) ? value!.answers : [],
  result: value?.result ?? null,
  status: value?.status === 'in_progress' || value?.status === 'completed' || value?.status === 'technical_interrupted'
    ? value.status
    : 'idle',
});

const loadInterviewDataForScope = (scope: InterviewScope): InterviewData => {
  const storageKey = buildInterviewStorageKey(scope);
  if (!storageKey) {
    return buildScopedDefaultData(scope);
  }

  const cached = readInterviewCache<InterviewData>(scope);
  return cached ? normalizeInterviewData(cached, scope) : buildScopedDefaultData(scope);
};

export const InterviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeScope, setActiveScope] = useState<InterviewScope>(() => ({
    userId: readAuthUserId(),
    jobId: null,
    outsourceId: null,
  }));
  const [interviewData, setInterviewData] = useState<InterviewData>(defaultData);
  const activeScopeRef = useRef(activeScope);

  useEffect(() => {
    activeScopeRef.current = activeScope;
  }, [activeScope]);

  const applyScope = useCallback((scope: { userId?: string | null; jobId?: number | null; outsourceId?: number | null }) => {
    const nextScope: InterviewScope = {
      userId: scope.userId ?? readAuthUserId(),
      jobId: normalizeScopeValue(scope.jobId),
      outsourceId: normalizeScopeValue(scope.outsourceId),
    };

    setActiveScope(nextScope);
    setInterviewData(loadInterviewDataForScope(nextScope));
  }, []);

  const bindInterviewScope = useCallback((scope: { jobId: number | null; outsourceId: number | null }) => {
    applyScope(scope);
  }, [applyScope]);

  const persist = useCallback((updater: (prev: InterviewData) => InterviewData) => {
    setInterviewData((prev) => {
      const next = normalizeInterviewData(updater(prev), activeScopeRef.current);
      writeInterviewCache(activeScopeRef.current, next);
      return next;
    });
  }, []);

  const setInterviewId = useCallback((id: number) => {
    persist((prev) => ({ ...prev, interviewId: id }));
  }, [persist]);

  const setJobId = useCallback((id: number) => {
    persist((prev) => ({ ...prev, jobId: id }));
  }, [persist]);

  const setOutsourceId = useCallback((id: number) => {
    persist((prev) => ({ ...prev, outsourceId: id }));
  }, [persist]);

  const setQuestions = useCallback((questions: InterviewQuestion[]) => {
    persist((prev) => ({ ...prev, questions }));
  }, [persist]);

  const addQuestion = useCallback((question: InterviewQuestion, options?: { insertAfterQuestionId?: number | null }) => {
    persist((prev) => {
      if (hasEquivalentQuestion(prev.questions, question)) {
        return prev;
      }

      const insertAfterId = Number(options && options.insertAfterQuestionId)
      if (!Number.isFinite(insertAfterId) || insertAfterId <= 0) {
        return { ...prev, questions: [...prev.questions, question] }
      }
      const currentIndex = prev.questions.findIndex((item) => Number(item.id) === insertAfterId)
      if (currentIndex === -1) {
        return { ...prev, questions: [...prev.questions, question] }
      }
      const nextQuestions = [...prev.questions]
      nextQuestions.splice(currentIndex + 1, 0, question)
      return { ...prev, questions: nextQuestions }
    })
  }, [persist]);

  const addAnswer = useCallback((answer: InterviewAnswer) => {
    persist((prev) => {
      const existingIndex = prev.answers.findIndex(
        (item) => item.question_id === answer.question_id
      );

      if (existingIndex === -1) {
        return { ...prev, answers: [...prev.answers, answer] };
      }

      const nextAnswers = [...prev.answers];
      nextAnswers[existingIndex] = answer;
      return { ...prev, answers: nextAnswers };
    });
  }, [persist]);

  const setResult = useCallback((result: InterviewResult) => {
    persist((prev) => ({ ...prev, result, status: 'completed' }));
  }, [persist]);

  const setStatus = useCallback((status: InterviewData['status']) => {
    persist((prev) => ({ ...prev, status }));
  }, [persist]);

  const resetInterview = useCallback(() => {
    clearInterviewCache(activeScopeRef.current);
    setInterviewData(buildScopedDefaultData(activeScopeRef.current));
  }, []);

  const clearUserInterviewCaches = useCallback(() => {
    clearInterviewCachesForUser(readAuthUserId());
    setInterviewData(loadInterviewDataForScope(activeScopeRef.current));
  }, []);

  useEffect(() => {
    clearLegacyInterviewStorage();

    const handleStorageSync = () => {
      clearLegacyInterviewStorage();
      const currentUserId = readAuthUserId();
      const previousScope = activeScopeRef.current;

      if (!currentUserId) {
        setActiveScope({ userId: null, jobId: null, outsourceId: null });
        setInterviewData(defaultData);
        return;
      }

      const nextScope: InterviewScope = {
        ...previousScope,
        userId: currentUserId,
      };

      setActiveScope(nextScope);
      setInterviewData(loadInterviewDataForScope(nextScope));
    };

    window.addEventListener('storage', handleStorageSync);
    return () => window.removeEventListener('storage', handleStorageSync);
  }, []);

  return (
    <InterviewContext.Provider value={{
      interviewData,
      bindInterviewScope,
      clearUserInterviewCaches,
      setInterviewId,
      setJobId,
      setOutsourceId,
      setQuestions,
      addQuestion,
      addAnswer,
      setResult,
      setStatus,
      resetInterview,
    }}>
      {children}
    </InterviewContext.Provider>
  );
};

export const useInterview = (): InterviewContextType => {
  const ctx = useContext(InterviewContext);
  if (!ctx) throw new Error('useInterview must be used inside InterviewProvider');
  return ctx;
};
